import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyParticipant } from '../services/api';

const getToken = () =>
  localStorage.getItem('govirtual_token') || sessionStorage.getItem('govirtual_token');

const clearAuth = () => {
  ['govirtual_token', 'govirtual_user'].forEach(k => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
};

/**
 * useAuthGuard
 * ------------
 * Verifies the participant token server-side on every protected page load.
 * When requireLogin=false, skips the redirect and marks auth as checked immediately.
 */
const useAuthGuard = (event, requireLogin = true) => {
  const navigate   = useNavigate();
  const hasRun     = useRef(false);
  const [authChecked, setAuthChecked] = useState(!requireLogin); // pre-checked when no login required
  const [isAuthed,    setIsAuthed]    = useState(false);

  useEffect(() => {
    // If login is not required, mark as checked immediately and don't redirect
    if (!requireLogin) {
      setAuthChecked(true);
      return;
    }

    if (!event?._id) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const redirectToLogin = () => {
      setIsAuthed(false);
      setAuthChecked(true);
      const loginPath = event.urlMode === 'flat'
        ? '/login'
        : `/${event.slug}/login`;
      navigate(loginPath, { replace: true });
    };

    const token = getToken();
    if (!token) {
      redirectToLogin();
      return;
    }

    verifyParticipant()
      .then(res => {
        if (res.data.success) {
          const store = localStorage.getItem('govirtual_token') ? localStorage : sessionStorage;
          store.setItem('govirtual_user', JSON.stringify(res.data.user));
          setIsAuthed(true);
          setAuthChecked(true);
        } else {
          clearAuth();
          redirectToLogin();
        }
      })
      .catch(() => {
        clearAuth();
        redirectToLogin();
      });
  }, [event?._id, requireLogin]);

  return { authChecked, isAuthed };
};

export default useAuthGuard;